import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiHook } from "../../hooks/apiHook";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import MyPayPalSubscriptionButton from "../payment/PaypalSubscriptionButton";
import Stripe from "../../views/payment/Stripe";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import "./NewSubscriptionCreator.css";

const NewSubscriptionCreator = ({
  onSubscriptionCreated,
  subscriptionPlans = [],
}) => {
  const { t } = useTranslation();
  const { setValue, watch } = useForm();

  const [activeTab, setActiveTab] = useState("");
  const [activeSlug, setActiveSlug] = useState("");
  const [submitButtonActive, setSubmitButtonActive] = useState(true);
  const [stripePublicKey, setStripePublicKey] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [stripeIntentStatus, setStripeIntentStatus] = useState("");
  const [paypalPlanId, setPaypalPlanId] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(null);

  const userSelectedCurrency = useSelector(
    (state) => state.user?.selectedCurrency
  );
  const conversionFactor = useSelector(
    (state) => state?.user?.conversionFactor
  );
  const user = useSelector((state) => state?.user?.loginResponse?.user);
  const username = user ? JSON.parse(user) : null;

  // API Hooks
  //   const subscriptionPlans = ApiHook.CallGetSubscriptionDetails();
  const paymentMethods = ApiHook.CallPaymentMethods("membership_renewal");
  const stripeIntent = ApiHook.CallCreateStripePaymentIntent();
  const updateStripeIntent = ApiHook.CallUpdateStripePaymentIntent(paymentIntentId);
  const cancelStripeIntent = ApiHook.CallCancelStripePaymentIntent();

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setValue("packageId", plan.packageId);
    setValue("totalAmount", plan.price);
    setActiveTab("");
    setActiveSlug("");
  };

  const handlePaymentTabClick = (tabId, slug) => {

    if (!selectedPlan) {
      toast.error("Please select a subscription plan first");
      return;
    }

    setActiveSlug(slug);
    setPaypalPlanId(paymentMethods.data?.paypalPlanId);
    setActiveTab(tabId);
    setValue("paymentMethod", tabId);

    if (slug === "stripe") {
      setSubmitButtonActive(true);
      setStripePublicKey(paymentMethods.data?.stripePublicKey);

      if (paymentIntentId === "") {
        const body = {
          productId: selectedPlan.packageId,
          type: "membership_renewal",
          autoRenewalStatus: true,
        };

        stripeIntent.mutateAsync(body, {
          onSuccess: (res) => {
            if (res.status) {
              setClientSecret(res.client_secret);
              setPaymentIntentId(res.id);
              setStripeIntentStatus(res.status);
              setSubscriptionId(res.subscriptionId);
            } else {
              toast.error(res.description);
            }
          },
        });
      }
    } else if (slug === "paypal") {
      setSubmitButtonActive(true);
    }
  };


const handleSubscriptionSuccess = (
  paymentId,
  paymentIntent,
  paymentType,
  paymentGatewayData
) => {
  // Validate that we have the minimum required data

  try {
    toast.success("Subscription created successfully!");
    
    // Pass all relevant data to parent
    if (onSubscriptionCreated) {
      onSubscriptionCreated({
        paymentType,
        paymentGatewayData,
        subscriptionId,
        selectedPlan: selectedPlan
      });
    }

    // Reset component state after successful creation
    setSelectedPlan(null);
    setActiveTab("");
    setActiveSlug("");
    setPaymentIntentId("");
    setClientSecret("");
    
  } catch (error) {
    toast.error("Error processing subscription. Please contact support.");
    console.error("Subscription success handler error:", error);
  }
};

  if (!subscriptionPlans || subscriptionPlans.length === 0) {
    return (
      <div className="new-subscription-loading">
        <div>Loading subscription plans...</div>
      </div>
    );
  }

  return (
    <div className="new-subscription-creator">
      <div className="new-subscription-header">
        <h2>Create New Subscription</h2>
        <p>Choose a plan and payment method to get started again</p>
      </div>

      {/* Plan Selection */}
      <div className="subscription-plans-section">
        <h3>Available Plans</h3>
        {subscriptionPlans.length > 0 && (
          <div className="subscription-plans-grid">
            {subscriptionPlans.map((plan) => (
              <div
                key={plan.id}
                className={`subscription-plan-card ${
                  selectedPlan?.id === plan.id ? "selected" : ""
                }`}
                onClick={() => handlePlanSelection(plan)}
              >
                <div className="plan-header">
                  <h4>{plan.name}</h4>
                  <br></br>
                  <div className="plan-price">
                    {`${userSelectedCurrency?.symbolLeft} ${CurrencyConverter(
                      plan.price,
                      conversionFactor
                    )} `}

                    <span className="plan-interval">
                      /{`${plan.validity} month`}
                    </span>
                  </div>
                </div>

                {selectedPlan?.id === plan.id && (
                  <div className="plan-selected-indicator">✓ Selected</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Auto Renewal Toggle */}
      {selectedPlan && (
        <div className="auto-renewal-section">
          <label className="auto-renewal-toggle">
            <span style={{ color: "#ff0000" }}>Please Note:</span>  Subscribing now means recurring payments will start from this month and will be automatically charged to your account.
          </label>
        </div>
      )}

      {/* Payment Methods */}
      {selectedPlan && paymentMethods?.data && (
        <div className="payment-methods-section">
          <h3>Choose Payment Method</h3>
          <div className="payment-methods-grid">
            {paymentMethods.data.methods?.map((method) => (
              <div
                key={method.id}
                className={`payment-method-card ${
                  activeTab === method.id ? "active" : ""
                }`}
                onClick={() => handlePaymentTabClick(method.id, method.slug)}
              >
                {console.log(method)}
                <div className="payment-method-icon">
                  <i className={method.icon}></i> {method.code}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Payment Processing */}
      {activeTab && selectedPlan && (
        <div className="payment-processing-section">
          {activeSlug === "stripe" && clientSecret && (
            <div className="stripe-payment-container">
              <Stripe
                clientSecret={clientSecret}
                publicKey={stripePublicKey}
                handleSubmit={handleSubscriptionSuccess}
                onError={(error) => toast.error(error)}
              />
            </div>
          )}

          {activeSlug === "paypal" && (
            <div className="paypal-payment-container">
              <MyPayPalSubscriptionButton
                key={true}
                data={{
                  paypalPlanId: paypalPlanId,
                }}
                // handleSubmit={handleSubscriptionSuccess}
                redirectUrl={"subscription-details"}
              />
              
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewSubscriptionCreator;
